import React, { useState, useRef } from "react";
import styled from "styled-components";
import {
  Button,
  FileInput,
  Group,
  Text,
  TextInput,
  Stack,
  ActionIcon,
  Tooltip,
  Textarea,
  Paper,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useMutation } from "@apollo/client";
import { useNavigate } from "react-router-dom";
import { CREATE_PROMOTION } from "../../gqls/promotion";
import {
  IconBold,
  IconItalic,
  IconMoodSmile,
  IconPhoto,
  IconTrash,
  IconArrowLeft,
} from "@tabler/icons-react";

export const CreatePromotion = () => {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [endDate, setEndDate] = useState("");
  const [file, setFile] = useState(undefined);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const descriptionRef = useRef(null);
  const [createPromotion] = useMutation(CREATE_PROMOTION);
  const navigate = useNavigate();

  const POPULAR_EMOJIS = [
    "🎉",
    "🎁",
    "⭐",
    "✨",
    "🔥",
    "💯",
    "👍",
    "💖",
    "🚀",
    "💰",
    "🛍️",
    "📦",
    "🎊",
    "🌟",
    "💝",
    "🎈",
  ];

  const ACCEPTED_IMAGE_FORMATS =
    "image/jpeg,image/png,image/gif,image/webp,.jpg,.jpeg,.png,.gif,.webp";

  const handleFileChange = (file) => {
    if (file) {
      const validFormats = [
        "image/jpeg",
        "image/png",
        "image/gif",
        "image/webp",
      ];
      if (!validFormats.includes(file.type)) {
        notifications.show({
          color: "red",
          title: "Неподдерживаемый формат",
          message: "Пожалуйста, используйте JPG, PNG, GIF или WebP",
        });
        return;
      }

      setFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result);
      };
      reader.readAsDataURL(file);
    } else {
      setFile(undefined);
      setPreview(null);
    }
  };

  const applyFormat = (format) => {
    const textarea = descriptionRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = description.substring(start, end);

    if (!selectedText) {
      notifications.show({
        color: "blue",
        title: "Выделите текст для форматирования",
      });
      return;
    }

    let formattedText = selectedText;
    if (format === "bold") {
      formattedText = `**${selectedText}**`;
    } else if (format === "italic") {
      formattedText = `*${selectedText}*`;
    }

    const newDescription =
      description.substring(0, start) +
      formattedText +
      description.substring(end);

    setDescription(newDescription);

    setTimeout(() => {
      textarea.focus();
      textarea.selectionStart = start + formattedText.length;
      textarea.selectionEnd = start + formattedText.length;
    }, 0);
  };

  const insertEmoji = (emoji) => {
    const textarea = descriptionRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const newDescription =
      description.slice(0, start) + emoji + description.slice(start);
    setDescription(newDescription);

    setTimeout(() => {
      textarea.focus();
      textarea.selectionStart = start + emoji.length;
      textarea.selectionEnd = start + emoji.length;
    }, 0);
  };

  const parseDescription = (text) => {
    let parsed = text;
    parsed = parsed.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
    parsed = parsed.replace(/\*(.+?)\*/g, "<em>$1</em>");
    return parsed;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!file) {
      notifications.show({
        color: "yellow",
        title: "Выберите изображение",
      });
      return;
    }

    if (!description.trim()) {
      notifications.show({
        color: "yellow",
        title: "Добавьте описание",
      });
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const photoResponse = await fetch("/api/admin/upload", {
        method: "POST",
        body: formData,
        headers: { authorization: localStorage.getItem("accessToken") },
      }).then((res) => res.json());

      if (!photoResponse?.filename) {
        notifications.show({
          color: "red",
          title: "Произошла ошибка при загрузке изображения",
        });
        setLoading(false);
        return;
      }

      const image = photoResponse.filename;

      await createPromotion({
        variables: {
          data: {
            title: title.trim(),
            description,
            image,
            endDate: endDate,
            products: [],
            active: false,
          },
        },
      });

      notifications.show({
        color: "green",
        title: "Акция успешно создана",
      });

      navigate("/promotions");
      setLoading(false);
    } catch (err) {
      console.error(err);
      notifications.show({
        color: "red",
        title: "Произошла ошибка, повторите еще раз",
      });
      setLoading(false);
    }
  };

  return (
    <Container>
      <Header>
        <Button
          variant="subtle"
          leftSection={<IconArrowLeft size={16} />}
          onClick={() => navigate("/promotions")}
          mb={16}
        >
          Вернуться к акциям
        </Button>
        <Text fw="600" size="lg">
          Добавить акцию
        </Text>
      </Header>

      <Form onSubmit={handleSubmit}>
        <TextInput
          type="text"
          placeholder="Введите заголовок..."
          label="Заголовок"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          maxLength={65}
          description={`Не обязательное поле (использовано ${title.length} из 65)`}
        />

        <TextInput
          type="date"
          label="Дата окончания баннера"
          placeholder="Выберите дату"
          mt={16}
          required
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          description="Формат: дд.мм.гг"
        />

        <Stack mt={16} gap={8}>
          <div>
            <Text component="label" fw={500} size="sm" mb={4}>
              Описание <span style={{ color: "red" }}>*</span>
            </Text>
            <Text size="xs" c="dimmed" mb={8}>
              Добавляйте форматирование и эмодзи
            </Text>

            <ToolbarGroup gap={8} mb={8}>
              <Tooltip label="Жирный текст (выделите и нажмите)">
                <ActionIcon
                  variant="light"
                  size="lg"
                  onClick={() => applyFormat("bold")}
                  aria-label="Bold"
                >
                  <IconBold size={16} />
                </ActionIcon>
              </Tooltip>

              <Tooltip label="Курсив (выделите и нажмите)">
                <ActionIcon
                  variant="light"
                  size="lg"
                  onClick={() => applyFormat("italic")}
                  aria-label="Italic"
                >
                  <IconItalic size={16} />
                </ActionIcon>
              </Tooltip>

              <Divider orientation="vertical" />

              <Tooltip label="Добавить эмодзи">
                <ActionIcon
                  variant="light"
                  size="lg"
                  onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                  aria-label="Emoji"
                >
                  <IconMoodSmile size={16} />
                </ActionIcon>
              </Tooltip>
            </ToolbarGroup>

            {showEmojiPicker && (
              <EmojiPanel>
                {POPULAR_EMOJIS.map((emoji) => (
                  <EmojiButton
                    key={emoji}
                    onClick={() => insertEmoji(emoji)}
                    type="button"
                  >
                    {emoji}
                  </EmojiButton>
                ))}
              </EmojiPanel>
            )}

            <Textarea
              ref={descriptionRef}
              placeholder="Введите описание акции..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              autosize
              minRows={6}
              maxRows={12}
              styles={{
                input: {
                  fontFamily: "monospace",
                  fontSize: "14px",
                },
              }}
            />

            <HelpText>
              💡 Используйте <code>**текст**</code> для жирного,{" "}
              <code>*текст*</code> для курсива
            </HelpText>
          </div>

          {description && (
            <Paper p={12} bg="gray.0" radius="md" withBorder>
              <Text size="sm" c="dimmed" mb={6}>
                Превью:
              </Text>
              <PreviewBox
                dangerouslySetInnerHTML={{
                  __html: parseDescription(description),
                }}
              />
            </Paper>
          )}
        </Stack>

        <ImageSection mt={24}>
          <Stack gap={12}>
            <div>
              <Text component="label" fw={500} size="sm" mb={4}>
                Изображение акции <span style={{ color: "red" }}>*</span>
              </Text>
              <Text size="xs" c="dimmed">
                Рекомендуемый размер: 310×240 px
              </Text>
            </div>

            <FileInput
              placeholder="Нажмите, чтобы выбрать изображение"
              value={file}
              onChange={handleFileChange}
              accept={ACCEPTED_IMAGE_FORMATS}
              icon={<IconPhoto size={14} />}
              clearable
              description="✓ Поддерживаются: JPG, PNG, GIF, WebP"
            />

            {preview && (
              <ImagePreviewWrapper>
                <ImagePreview src={preview} alt="Preview" />
                <RemoveButton
                  size="sm"
                  color="red"
                  variant="light"
                  leftSection={<IconTrash size={14} />}
                  onClick={() => {
                    setFile(undefined);
                    setPreview(null);
                  }}
                >
                  Удалить
                </RemoveButton>
              </ImagePreviewWrapper>
            )}
          </Stack>
        </ImageSection>

        <ButtonWrapper>
          <Button loading={loading} type="submit" size="md" fullWidth>
            Добавить акцию
          </Button>
        </ButtonWrapper>
      </Form>
    </Container>
  );
};

// ============================================
// STYLED COMPONENTS
// ============================================

const Container = styled.div`
  padding: 0;
`;

const Header = styled.div`
  margin-bottom: 24px;
`;

const Form = styled.form`
  max-width: 600px;
  margin-top: 0;
`;

const ToolbarGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px;
  background-color: #f9f9f9;
  border-radius: 6px;
  border: 1px solid #e9ecef;
`;

const Divider = styled.div`
  width: 1px;
  height: 24px;
  background-color: #dee2e6;
`;

const EmojiPanel = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(40px, 1fr));
  gap: 6px;
  padding: 12px;
  background-color: #f9f9f9;
  border-radius: 6px;
  border: 1px solid #e9ecef;
  margin-bottom: 12px;
  max-height: 200px;
  overflow-y: auto;
`;

const EmojiButton = styled.button`
  font-size: 24px;
  background: none;
  border: 1px solid transparent;
  border-radius: 4px;
  padding: 4px 8px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: #e9ecef;
    border-color: #dee2e6;
    transform: scale(1.1);
  }

  &:active {
    transform: scale(0.95);
  }
`;

const HelpText = styled.div`
  margin-top: 8px;
  font-size: 12px;
  color: #868e96;

  code {
    background-color: #f1f1f1;
    padding: 2px 6px;
    border-radius: 3px;
    font-family: "Courier New", monospace;
    font-size: 11px;
  }
`;

const PreviewBox = styled.div`
  font-size: 14px;
  line-height: 1.6;
  color: #333;

  strong {
    font-weight: 700;
  }

  em {
    font-style: italic;
    color: #495057;
  }
`;

const ImageSection = styled.div`
  padding: 16px;
  background-color: #f9f9f9;
  border-radius: 8px;
  border: 1px solid #e9ecef;
`;

const ImagePreviewWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: flex-start;
`;

const ImagePreview = styled.img`
  width: 310px;
  height: 240px;
  object-fit: cover;
  border-radius: 8px;
  border: 2px solid #dee2e6;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const RemoveButton = styled(Button)``;

const ButtonWrapper = styled.div`
  margin-top: 24px;
  display: flex;
  gap: 12px;
`;
